import { HelpCircle, Search } from '@ori-ui/icons';
import type { MenuItemTypeMap, TextFieldProps } from '@ori-ui/mui';
import { Tooltip, Paper, MenuItem, TextField, InputAdornment, styled } from '@ori-ui/mui';
import type {
  GetAddressSuggestionsParams,
  GlobalAddressesLookupAddressNodeDto,
  GlobalAddressesLookupAddressSearchResultDto,
  GlobalAddressesLookupAddressSuggestionsDto,
  GlobalCustomerRegistrationsRegistrationPersonalDetailsDto,
  GlobalCustomerRegistrationsRegistrationSponsorDetailsDto,
  GlobalCustomersAddressDto,
  GlobalCustomersMetadataAddressMetadataDto,
} from '@ori/global-api-client-react';
import { getAddressSearchResult, getAddressSuggestions, useApiClient } from '@ori/global-api-client-react';
import useTestId from '@ori/testid-generator';
import type { ControllerStateAndHelpers, GetInputPropsOptions } from 'downshift';
import Downshift from 'downshift';
import type { KeyboardEvent, FC, ReactNode } from 'react';
import React, { useState } from 'react';
import type { PathValue, Path } from 'react-hook-form';
import { useFormContext } from 'react-hook-form';

import { Localization } from '../../Localization';
import { defaultTranslations } from '../../constants/defaultTranslations_simplified-registration-BrandPartner';
import { useNotificationData } from '../../contexts';
import { useTranslations } from '../../hooks/useTranslations_simplified-registration-BrandPartner';
import type { IAddressNodesFetchParams } from '../../interfaces';
import { MessageType } from '../../interfaces';
import { FetchResult, TranslationKey } from '../../models';

const Elevation = 8;

type AddressWithNodeId = GlobalCustomersAddressDto & {
  nodeId?: string;
  addressSearchResultId?: string;
};

type RenderInputProps = TextFieldProps & { ref?: React.Ref<HTMLDivElement> };

type FormDataAddressType = GlobalCustomerRegistrationsRegistrationSponsorDetailsDto['address'];

export interface IAddressSuggestionListItem {
  addressSummary?: string;
  description?: string;
  count: number;
  pathKey?: string;
  addressNodeId?: string;
}

export interface ISuggestionProps {
  itemProps: MenuItemTypeMap<{ button: false }>;
  suggestion: IAddressSuggestionListItem;
}

export interface IAddressAutocompleteProps {
  minSearchLength?: number;
  isEditable?: boolean;
  tooltipText?: ReactNode;
  searchedFields?: string[];
}

const StyledHelpCircle = styled(HelpCircle)(({ theme }) => ({
  fontSize: '30px',
  color: theme.palette.grey[300],
}));

export const AddressAutocomplete: FC<GlobalCustomersMetadataAddressMetadataDto & IAddressAutocompleteProps> = (
  props,
) => {
  const { searchOptions, isEditable, tooltipText, searchedFields } = props;
  const { formatMessage } = useTranslations();
  const api = useApiClient();
  const form = useFormContext<
    GlobalCustomerRegistrationsRegistrationPersonalDetailsDto | GlobalCustomerRegistrationsRegistrationSponsorDetailsDto
  >();
  const { getTestId } = useTestId();

  const {
    context: { notificationData },
  } = useNotificationData();
  const { setNotificationData } = notificationData;
  const values = form.watch();
  const errorKeys = Object.keys(form.formState.errors.address ?? {});

  const isErrors = searchedFields?.some((sf) => errorKeys.includes(sf));

  const [value, setValue] = useState('');
  const [suggestions, setSuggestions] = useState<IAddressSuggestionListItem[]>([]);
  const [isOpenSelect, setOpen] = useState(false);
  const [isItemClicked, setItemClicked] = useState(false);
  const [lastSearchedText, setLastSearchedText] = useState('');

  const countryValue = values.address?.country;

  let minSearchLength = 3;
  const defaultMaxResults = 10;

  if (props.minSearchLength) {
    minSearchLength = props.minSearchLength;
  }

  const handleSearchTextChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    setValue(event.target.value);
  };

  const search = async (
    options: IAddressNodesFetchParams,
  ): Promise<FetchResult<GlobalAddressesLookupAddressSuggestionsDto>> => {
    const { searchText, top, country, pathKey }: IAddressNodesFetchParams = options;
    const emptySuggestionsResponse: GlobalAddressesLookupAddressSuggestionsDto = {
      addressSuggestionsCollection: [],
    };

    if (!searchText) {
      return FetchResult.errorFor(emptySuggestionsResponse);
    }
    const apiParams: GetAddressSuggestionsParams = {
      SearchText: searchText,
      Top: top,
      Country: country,
      PathKey: pathKey,
    };

    try {
      const nodes = await getAddressSuggestions(api, apiParams);

      return new FetchResult<GlobalAddressesLookupAddressSuggestionsDto>(nodes.status, nodes.data);
    } catch (error: unknown) {
      setNotificationData({
        isOpen: true,
        message: (
          <Localization
            id={TranslationKey.cannotGetData}
            defaultMessage={defaultTranslations.cannotGetData}
          />
        ),
        type: MessageType.error,
      });
    }

    return FetchResult.errorFor(emptySuggestionsResponse);
  };

  const getAddress = async (
    nodeId: string,
    country?: string,
  ): Promise<FetchResult<GlobalAddressesLookupAddressSearchResultDto>> => {
    const emptyAddressResponse: GlobalAddressesLookupAddressSearchResultDto = {
      type: 'Area',
    };

    try {
      const addressNode = await getAddressSearchResult(api, nodeId, { country });

      return new FetchResult<GlobalAddressesLookupAddressSearchResultDto>(addressNode.status, addressNode.data);
    } catch (error: unknown) {
      setNotificationData({
        isOpen: true,
        message: (
          <Localization
            id={TranslationKey.cannotGetData}
            defaultMessage={defaultTranslations.cannotGetData}
          />
        ),
        type: MessageType.error,
      });
    }

    return FetchResult.errorFor(emptyAddressResponse);
  };

  const getStateAddressSuggestions = async (
    searchText: string,
    pathKey?: string,
  ): Promise<IAddressSuggestionListItem[] | null> => {
    if (searchText.length >= minSearchLength) {
      const addressNodesResult = await search({
        searchText,
        top: searchOptions?.maxResults ?? defaultMaxResults,
        country: countryValue,
        pathKey,
      });
      const { response } = addressNodesResult;

      if (response?.addressSuggestionsCollection) {
        const addressSuggestions: IAddressSuggestionListItem[] = response.addressSuggestionsCollection.map((node) => ({
          addressSummary: node.addressSummary,
          description: node.description,
          count: node.count,
          pathKey: node.pathKey,
          addressNodeId: node.addressNodeId,
        }));

        return addressSuggestions;
      }

      return null;
    }

    return [];
  };

  const handleOnClick = async (): Promise<void> => {
    if (value !== '' && !isOpenSelect) {
      const suggestedAddresses = await getStateAddressSuggestions(value);
      if (suggestedAddresses != null) {
        setSuggestions(suggestedAddresses);
        setOpen(true);
      }
    }
  };

  const renderInput = (inputProps: RenderInputProps) => {
    const { InputProps, ref, ...other }: RenderInputProps = inputProps;
    const componentProps = {
      inputRef: ref,
      ...InputProps,
      autoComplete: 'off',
    };

    return (
      <TextField
        InputProps={componentProps}
        {...other}
        // eslint-disable-next-line react/jsx-no-duplicate-props --- we needed is not duplicate props
        inputProps={{
          'data-testid': getTestId('autocomplete-search-field'),
          ...other.inputProps,
        }}
        disabled={!isEditable}
        variant="outlined"
        onChange={handleSearchTextChange}
        onClick={handleOnClick}
      />
    );
  };

  const renderAddressSuggestionItem = (suggestion: IAddressSuggestionListItem): JSX.Element => {
    const addressSummary = `${suggestion.addressSummary} `;

    return (
      <div>
        <span style={{ fontWeight: 'bold' }}>{suggestion.description}</span>
        <br />
        {addressSummary}
        {suggestion.count !== 1 && (
          <Localization
            id={TranslationKey.addresResults}
            defaultMessage={defaultTranslations.addresResults}
            values={{ count: suggestion.count }}
          />
        )}
      </div>
    );
  };

  const renderSuggestion = (suggestionProps: ISuggestionProps) => {
    const { suggestion, itemProps }: ISuggestionProps = suggestionProps;

    return (
      <MenuItem
        {...itemProps}
        component="div"
        style={{
          fontWeight: 'normal',
          whiteSpace: 'unset',
          wordBreak: 'break-word',
        }}
      >
        {renderAddressSuggestionItem(suggestion)}
      </MenuItem>
    );
  };

  const renderSuggestions = (options: ControllerStateAndHelpers<IAddressSuggestionListItem>) => {
    const { getItemProps }: ControllerStateAndHelpers<IAddressSuggestionListItem> = options;
    let items = [
      <MenuItem
        key={0}
        component="div"
      >
        <Localization
          id={TranslationKey.noSuggestions}
          defaultMessage={defaultTranslations.noSuggestions}
        />
      </MenuItem>,
    ];

    if (suggestions.length > 0) {
      items = suggestions.map((suggestion, index) => {
        const suggestionProps = {
          index,
          suggestion,
          itemProps: getItemProps({ item: suggestion }) as MenuItemTypeMap<{ button: false }>,
        };

        return renderSuggestion(suggestionProps);
      });
    }

    return (
      <Paper
        style={{
          position: 'absolute',
          zIndex: 10,
          top: 'calc(100% - 10px)',
          right: 0,
          left: 0,
          padding: '8px 0',
          overflow: 'hidden',
          background: 'white',
          maxHeight: '300px',
          overflowY: 'auto',
          textAlign: 'justify',
        }}
        elevation={Elevation}
      >
        {items}
      </Paper>
    );
  };

  const mapNodeToAddress = (node: GlobalAddressesLookupAddressNodeDto): AddressWithNodeId => {
    const result: AddressWithNodeId = values.address as GlobalCustomersAddressDto;

    Object.keys(node)
      .filter((nodeKey) => node[nodeKey as keyof GlobalAddressesLookupAddressNodeDto])
      .forEach((nodeKey) => {
        const addressNodeKey = nodeKey as keyof GlobalAddressesLookupAddressNodeDto;
        const addressValue = node[addressNodeKey] as string;
        if (addressNodeKey === 'street') {
          result.streetAddress = addressValue;
        } else if (addressNodeKey === 'zipCode') {
          result.zipcode = addressValue;
        } else if (addressNodeKey !== 'type' && addressNodeKey !== 'buildingDetails') {
          result[addressNodeKey] = addressValue;
        }
      });

    return result;
  };

  const handleAutocompleteKeyUp = async (event: KeyboardEvent): Promise<void> => {
    const searchInput = event.target as HTMLInputElement;
    if (searchInput.value === '') {
      setSuggestions([]);
      setValue('');
      setOpen(false);
    }

    const searchText = searchInput.value.trimStart();
    setValue(searchText);
    setLastSearchedText(searchText);
    const suggestedAddresses = await getStateAddressSuggestions(searchText);
    if (suggestedAddresses != null && searchText !== lastSearchedText) {
      setSuggestions(suggestedAddresses);
      setOpen(true);
    }
  };

  const getAutocompleteContent = (options: ControllerStateAndHelpers<IAddressSuggestionListItem>) => {
    const { getInputProps, isOpen }: ControllerStateAndHelpers<IAddressSuggestionListItem> = options;

    const { onBlur, onFocus, ...inputProps }: GetInputPropsOptions = getInputProps({
      onKeyUp: handleAutocompleteKeyUp,
      placeholder: formatMessage(TranslationKey.searchForAnAddress),
    });
    const mergedInputProps: RenderInputProps = {
      InputProps: {
        onBlur,
        onFocus,
        startAdornment: (
          <InputAdornment position="start">
            <Search />
          </InputAdornment>
        ),
        endAdornment: tooltipText ? (
          <Tooltip
            title={tooltipText}
            enterTouchDelay={0}
            leaveTouchDelay={5000}
          >
            <StyledHelpCircle />
          </Tooltip>
        ) : undefined,
      },
      fullWidth: true,
      inputProps: { ...inputProps, autoComplete: 'off', value },
      error: isErrors,
      required: true,
      label: (
        <Localization
          id={TranslationKey.addressSearch}
          defaultMessage={defaultTranslations.addressSearch}
        />
      ),
      helperText: isErrors ? (
        <Localization
          id={TranslationKey.addressSearchValidation}
          defaultMessage={defaultTranslations.addressSearchValidation}
        />
      ) : null,
    };

    return (
      <div style={{ position: 'relative' }}>
        {renderInput(mergedInputProps)}
        {isOpen ? renderSuggestions(options) : null}
      </div>
    );
  };

  const handleAddressSuggestionItemSelect = async (item: IAddressSuggestionListItem | null): Promise<void> => {
    if (isItemClicked) {
      return;
    }
    try {
      setItemClicked(true);
      if (item && item.pathKey == null) {
        if (item.addressNodeId) {
          const address = await getAddress(item.addressNodeId, countryValue);

          setValue('');
          setSuggestions([]);
          setOpen(false);
          if (address.response) {
            const correctAddress = Object.entries(mapNodeToAddress(address.response)) as Array<
              [Path<FormDataAddressType>, PathValue<FormDataAddressType, Path<FormDataAddressType>>]
            >;
            correctAddress.forEach((entry) => {
              const [name, inputValue] = entry;

              form.setValue(`address.${name}`, inputValue, {
                shouldDirty: true,
                shouldTouch: true,
                shouldValidate: searchedFields?.includes(name),
              });
            });
          }
        }
      } else {
        const suggestedAddresses = await getStateAddressSuggestions(value, item?.pathKey);
        if (suggestedAddresses != null) {
          setSuggestions(suggestedAddresses);
          setOpen(true);
        }
      }
    } finally {
      setItemClicked(false);
    }
  };

  const handleDownshiftOuterClick = () => {
    setOpen(false);
  };

  return (
    <Downshift
      defaultHighlightedIndex={null}
      selectedItem={null}
      isOpen={isOpenSelect}
      onSelect={handleAddressSuggestionItemSelect}
      onOuterClick={handleDownshiftOuterClick}
    >
      {(options) => getAutocompleteContent(options)}
    </Downshift>
  );
};
